// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null); // decoded token
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const login = (newToken) => {
    localStorage.setItem('authToken', newToken);
    try {
      const decoded = jwtDecode(newToken);
      console.log('Decoded token:', decoded);
      
      // Ensure role is properly extracted from JWT
      const userData = {
        ...decoded,
        // Ensure the role field exists and is formatted correctly
        role: decoded.role ? decoded.role.toUpperCase() : null
      };
      
      setToken(newToken);
      setUser(userData);
      setIsAuthenticated(true);
      
      console.log('User role set to:', userData.role);
    } catch (err) {
      console.error("Invalid token", err);
      logout();
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  // Check if user has the required role
  const hasRole = (requiredRoles) => {
    if (!user || !user.role) {
      console.log('No user or role found:', user);
      return false;
    }
    
    // Admin role has access to everything
    if (user.role === 'ADMIN') {
      console.log('User has ADMIN role, granting access');
      return true;
    }
    
    // If no specific roles are required, the user is authorized
    if (!requiredRoles || requiredRoles.length === 0) return true;
    
    // Check if user role is in the required roles
    const hasRequiredRole = requiredRoles.includes(user.role);
    console.log(`Checking if ${user.role} is in [${requiredRoles.join(', ')}]: ${hasRequiredRole}`);
    return hasRequiredRole;
  };

  useEffect(() => {
    const loadAuthState = () => {
      setIsLoading(true);
      const storedToken = localStorage.getItem('authToken');
      
      if (storedToken) {
        try {
          const decoded = jwtDecode(storedToken);
          console.log('Loading stored token, decoded:', decoded);
          
          // Check if token is expired
          const currentTime = Date.now() / 1000;
          if (decoded.exp && decoded.exp < currentTime) {
            console.log('Token expired, logging out');
            logout();
          } else {
            // Ensure role is properly formatted
            const userData = {
              ...decoded,
              role: decoded.role ? decoded.role.toUpperCase() : null
            };
            
            setToken(storedToken);
            setUser(userData);
            setIsAuthenticated(true);
            console.log('Auth state loaded, user role:', userData.role);
          }
        } catch (err) {
          console.error("Invalid token", err);
          logout();
        }
      } else {
        console.log('No auth token found in localStorage');
      }
      
      setIsLoading(false);
    };
    
    loadAuthState();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        login,
        logout,
        hasRole,
        auth: { token, user },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
